import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { ArrowLeft, Download, Users, CheckCircle, BarChart3, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '../ui';
import { LoadingState } from '../common';
import { fetchSurveyResults, downloadSurveyReport } from '../../api/survey.api';

// Couleurs Bakeli
const COLORS = [
  '#F97316', '#22C55E', '#3B82F6', '#A855F7',
  '#EF4444', '#14B8A6', '#F59E0B', '#6366F1',
];

//Tooltip 
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      <p style={{ color: payload[0]?.fill || payload[0]?.color }}>
        Réponses : <strong>{payload[0]?.value}</strong>
        {payload[0]?.payload?.percentage !== undefined && ` (${payload[0].payload.percentage}%)`}
      </p>
    </div>
  );
};

//KPI Card 
const StatCard = ({ icon: Icon, label, value, color = 'text-primary' }) => (
  <Card className="shadow-card border-0">
    <CardContent className="p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div>
        <p className={`text-xl font-bold font-display ${color}`}>{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

//Label au-dessus des barres 
const BarLabel = ({ x, y, width, value }) => {
  if (value === 0 || value === undefined) return null;
  return (
    <text x={x + width / 2} y={y - 5} fill="#555" textAnchor="middle" fontSize={11} fontWeight={600}>
      {value}
    </text>
  );
};

//SINGLE / MULTIPLE / YES_NO
const ChoiceChart = ({ question, index }) => {
  const choices = question.choices_breakdown || [];
  const total   = question.responses_count   || 0;

  // Tous les choix — même ceux à 0
  const barData = choices.map((c, i) => ({
    name:       c.text,
    count:      c.count,
    percentage: c.percentage,
    fill:       COLORS[i % COLORS.length],
  }));

  // Pie uniquement sur les choix avec votes
  const pieData = choices
    .filter(c => c.count > 0)
    .map((c, i) => ({ name: c.text, value: c.count, fill: COLORS[i % COLORS.length] }));

  // Domaine Y dynamique
  const maxCount = Math.max(...choices.map(c => c.count), 1);
  const yMax     = maxCount <= 1 ? 3 : maxCount + Math.ceil(maxCount * 0.3);

  return (
    <Card className="shadow-card border-0 animate-slide-up">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="font-display text-base leading-snug">
            Q{index + 1}. {question.text}
          </CardTitle>
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 shrink-0 text-xs">
            {question.question_type === 'SINGLE_CHOICE'    ? 'QCM'
             : question.question_type === 'MULTIPLE_CHOICE' ? 'Choix multiples'
             : 'Oui / Non'}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{total} réponse{total > 1 ? 's' : ''}</p>
      </CardHeader>

      <CardContent className="space-y-5">
        {total === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Aucune réponse pour cette question</p>
        ) : (
          <>
            {/* ── Bar chart : TOUS les choix visibles ── */}
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={barData}
                margin={{ top: 24, right: 10, left: -10, bottom: 5 }}
                barCategoryGap="35%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#666' }}
                  stroke="hsl(var(--border))"
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  domain={[0, yMax]}
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#666' }}
                  stroke="hsl(var(--border))"
                  tickLine={false}
                  axisLine={false}
                  tickCount={yMax + 1}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} label={<BarLabel />} isAnimationActive>
                  {barData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} fillOpacity={entry.count === 0 ? 0.25 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* ── Légende avec % ── */}
            <div className="flex flex-wrap gap-2 justify-center">
              {choices.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5"
                  style={{ background: `${COLORS[i % COLORS.length]}15` }}
                >
                  <div
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: COLORS[i % COLORS.length], opacity: c.count === 0 ? 0.35 : 1 }}
                  />
                  <span className="text-muted-foreground">{c.text}</span>
                  <span className="font-bold" style={{ color: COLORS[i % COLORS.length] }}>{c.percentage}%</span>
                  <span className="text-muted-foreground">({c.count})</span>
                </div>
              ))}
            </div>

            {/* ── Pie chart ── */}
            {pieData.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    isAnimationActive
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} réponse(s)`, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

//SCALE
const ScaleChart = ({ question, index }) => {
  const responses    = question.responses    || [];
  const averageScore = question.average_score;
  const total        = question.responses_count || 0;

  const dist = {};
  responses.forEach(r => {
    const note = r.answer_text || '?';
    dist[note] = (dist[note] || 0) + 1;
  });
  const barData  = Object.entries(dist).sort(([a], [b]) => Number(a) - Number(b)).map(([note, count]) => ({ note, count }));
  const maxCount = Math.max(...Object.values(dist), 1);
  const yMax     = maxCount <= 1 ? 3 : maxCount + Math.ceil(maxCount * 0.3);

  return (
    <Card className="shadow-card border-0 animate-slide-up">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="font-display text-base leading-snug">
            Q{index + 1}. {question.text}
          </CardTitle>
          <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 shrink-0 text-xs">Échelle</Badge>
        </div>
        <p className="text-xs text-muted-foreground">{total} réponse{total > 1 ? 's' : ''}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {total === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Aucune réponse pour cette question</p>
        ) : (
          <>
            {averageScore !== null && averageScore !== undefined && (
              <div className="text-center py-4 bg-accent/5 rounded-xl">
                <p className="text-5xl font-bold font-display text-accent">{averageScore}</p>
                <p className="text-xs text-muted-foreground mt-1">Score moyen / 10</p>
                <div className="w-full bg-muted rounded-full h-2.5 mt-3 max-w-xs mx-auto">
                  <div className="h-2.5 rounded-full bg-accent transition-all duration-700" style={{ width: `${(averageScore / 10) * 100}%` }} />
                </div>
              </div>
            )}
            {barData.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={barData} margin={{ top: 24, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="note" tick={{ fontSize: 11 }} stroke="hsl(var(--border))" tickLine={false} />
                  <YAxis domain={[0, yMax]} allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Bar dataKey="count" fill="#22C55E" radius={[6, 6, 0, 0]} label={<BarLabel />} isAnimationActive />
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


//TEXT
const TextResponses = ({ questions }) => {
  const textQs = questions.filter(q => q.question_type === 'TEXT');
  if (!textQs.length) return null;

  return (
    <Card className="lg:col-span-2 shadow-card border-0 animate-slide-up">
      <CardHeader>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <CardTitle className="font-display text-base">Réponses texte libres</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {textQs.map((q) => (
          <div key={q.id}>
            <p className="text-sm font-semibold text-foreground mb-3">
              {q.text}
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({q.responses_count} réponse{q.responses_count > 1 ? 's' : ''})
              </span>
            </p>
            {q.responses?.length > 0 ? (
              <div className="space-y-2">
                {q.responses.map((r, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50 text-sm border-l-2 border-primary/30">
                    <p className="text-xs text-muted-foreground mb-1 font-medium">
                      {r.participant?.prenom} {r.participant?.nom}
                    </p>
                    <p className="text-foreground italic">"{r.answer_text}"</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucune réponse texte</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

//PAGE PRINCIPALE
const Results = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [exporting, setExporting]   = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetchSurveyResults(id);
        setSurveyData(response?.data || response);
      } catch (err) {
        setError('Impossible de charger les résultats');
        toast.error('Erreur lors du chargement des résultats');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  const handleExport = async (format) => {
    try {
      setExporting(format);
      toast.info(`Préparation de l'export ${format.toUpperCase()}...`);
      const { blob, filename } = await downloadSurveyReport(id, format);
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success(`Export ${format.toUpperCase()} téléchargé !`);
    } catch (err) {
      const code = err.response?.status;
      if (code === 404)      toast.error('Export non disponible pour ce sondage.');
      else if (code === 400) toast.error('Format non supporté. Utilisez PDF ou Excel.');
      else if (code === 403) toast.error('Accès refusé pour cet export.');
      else                   toast.error(`Erreur lors de l'export.`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) return <LoadingState message="Chargement des résultats..." />;

  if (error || !surveyData) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />Retour
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error || 'Aucune donnée disponible'}</p>
        </div>
      </div>
    );
  }

  const survey    = surveyData?.survey    || {};
  const stats     = surveyData?.stats     || {};
  const questions = surveyData?.questions || [];
  const hasTextQ  = questions.some(q => q.question_type === 'TEXT');

  return (
    <div className="space-y-6">

      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />Retour au sondage
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Résultats</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {survey.title} — {stats.total_responses || 0} réponse{(stats.total_responses || 0) > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => handleExport('pdf')} disabled={exporting !== null}>
            <Download className="h-4 w-4" />{exporting === 'pdf' ? 'Export...' : 'PDF'}
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => handleExport('excel')} disabled={exporting !== null}>
            <Download className="h-4 w-4" />{exporting === 'excel' ? 'Export...' : 'Excel'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Users}         label="Participants"       value={stats.total_participants || 0} />
        <StatCard icon={CheckCircle}   label="Ont répondu"        value={stats.responded || 0}         color="text-green-600" />
        <StatCard icon={BarChart3}     label="Taux de complétion" value={`${stats.participation_rate || 0}%`} color="text-primary" />
        <StatCard icon={MessageSquare} label="Réponses totales"   value={stats.total_responses || 0}   color="text-accent" />
      </div>

      <Card className="shadow-card border-0">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Progression globale</span>
            <span className="text-sm font-bold text-primary">{stats.participation_rate || 0}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div className="h-3 rounded-full bg-primary transition-all duration-700" style={{ width: `${Math.min(stats.participation_rate || 0, 100)}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {stats.responded || 0} participant{(stats.responded || 0) > 1 ? 's' : ''} sur {stats.total_participants || 0} ont répondu
          </p>
        </CardContent>
      </Card>

      {(stats.total_responses || 0) === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-xl">
          <BarChart3 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">Aucune réponse encore reçue</p>
          <p className="text-sm text-muted-foreground mt-1">Les graphiques apparaîtront dès les premières réponses</p>
        </div>
      )}

      {(stats.total_responses || 0) > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {questions.map((question, idx) => {
            if (['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'YES_NO'].includes(question.question_type))
              return <ChoiceChart key={question.id} question={question} index={idx} />;
            if (question.question_type === 'SCALE')
              return <ScaleChart key={question.id} question={question} index={idx} />;
            return null;
          })}
          {hasTextQ && <TextResponses questions={questions} />}
        </div>
      )}

    </div>
  );
};

export default Results;



// import { useEffect, useState } from 'react'
// import { Button, Badge, Card, CardContent, CardHeader, CardTitle } from '../ui'
// import { ArrowLeft, BarChart, Download, PieChart } from 'lucide-react'
// import { Bar, CartesianGrid, Cell, Pie, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
// import { useNavigate, useParams } from 'react-router-dom'
// import { toast } from 'react-toastify'
// import { fetchSurveyResults, downloadSurveyReport } from '../../api/survey.api'
// import { LoadingState } from '../common'

// const COLORS = [
//   "hsl(174, 100%, 29%)",
//   "hsl(36, 100%, 50%)",
//   "hsl(210, 20%, 70%)",
//   "hsl(354, 70%, 54%)",
// ];

// const Results = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [surveyData, setSurveyData] = useState(null);

//   useEffect(() => {
//     const loadResults = async () => {
//       try {
//         setLoading(true);
//         const response = await fetchSurveyResults(id);
//         setSurveyData(response?.data || response);
//       } catch (err) {
//         console.error('Erreur lors du chargement des résultats:', err);
//         setError('Erreur lors du chargement des résultats');
//         toast.error('Erreur lors du chargement des résultats');
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (id) {
//       loadResults();
//     }
//   }, [id]);

//   const handleExportCSV = async () => {
//     try {
//       toast.info('Préparation de l\'export...');
//       const { blob, filename } = await downloadSurveyReport(id, 'csv');
//       const url = window.URL.createObjectURL(blob);
//       const a = document.createElement('a');
//       a.href = url;
//       a.download = filename;
//       document.body.appendChild(a);
//       a.click();
//       window.URL.revokeObjectURL(url);
//       document.body.removeChild(a);
//       toast.success('Export téléchargé !');
//     } catch (err) {
//       console.error('Erreur lors de l\'export:', err);
//       toast.error('Erreur lors de l\'export : ' + (err.response?.status === 404 ? 'Endpoint non trouvé' : err.message));
//     }
//   };

//   if (loading) {
//     return <LoadingState message="Chargement des résultats..." />;
//   }

//   if (error || !surveyData) {
//     return (
//       <div className="space-y-6">
//         <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
//           <ArrowLeft className="h-4 w-4" />Retour
//         </Button>
//         <div className="text-center py-12">
//           <p className="text-red-600">{error || 'Aucune donnée disponible'}</p>
//         </div>
//       </div>
//     );
//   }

//   const survey = surveyData?.survey || {};
//   const stats = surveyData?.stats || {};
//   const questions = surveyData?.questions || [];

//   return (
//     <div className='space-y-6'>
//       <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
//         <ArrowLeft className="h-4 w-4" />Retour au sondage
//       </Button>

//       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
//         <div>
//           <h1 className="font-display text-2xl font-bold text-foreground">Résultats</h1>
//           <p className="text-sm text-muted-foreground mt-1">
//             {survey.title} — {stats.total_responses || 0} réponses
//           </p>
//         </div>
//         <Button variant="outline" className="gap-2" onClick={handleExportCSV}>
//           <Download className="h-4 w-4" />
//           Exporter CSV
//         </Button>
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//         {questions.map((question, idx) => {
//           // QCM - Pie Chart
//           if (question.choices_breakdown && question.choices_breakdown.length > 0) {
//             const pieData = question.choices_breakdown.map(choice => ({
//               name: choice.text,
//               value: choice.count
//             }));

//             console.log({pieData})
            
//             return (
//               <Card key={question.id} className="shadow-card border-0 animate-slide-up">
//                 <CardHeader>
//                   <div className="flex items-center justify-between">
//                     <CardTitle className="font-display text-base">
//                       Q{idx + 1}. {question.text}
//                     </CardTitle>
//                     <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
//                       {question.question_type === "SINGLE_CHOICE" ? "QCM" : "Choix multiples"} 
//                     </Badge>
//                   </div>
//                 </CardHeader>
//                 <CardContent>
//                   <ResponsiveContainer width="100%" height={250}>
//                     {/* <PieChart>
//                       <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
//                         {pieData.map((_, i) => (
//                           <Cell key={i} fill={COLORS[i % COLORS.length]} />
//                         ))}
//                       </Pie>
//                       <Tooltip />
//                     </PieChart> */}
//                   </ResponsiveContainer>
//                   <div className="flex flex-wrap gap-3 mt-4 justify-center">
//                     {pieData.map((item, i) => (
//                       <div key={item.name} className="flex items-center gap-1.5 text-xs">
//                         <div className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} />
//                         <span className="text-muted-foreground">{item.name}</span>
//                         <span className="font-semibold">{Math.round((item.value / question.responses_count) * 100)}%</span>
//                       </div>
//                     ))}
//                   </div>
//                 </CardContent>
//               </Card>
//             );
//           }

//           // Scale - Bar Chart
//           if (question.question_type === 'SCALE') {
//             const barData = question.responses.reduce((acc, resp) => {
//               const note = resp.answer_text || '?';
//               const existing = acc.find(item => item.note === note);
//               if (existing) {
//                 existing.count += 1;
//               } else {
//                 acc.push({ note, count: 1 });
//               }
//               return acc;
//             }, []);

//             return (
//               <Card key={question.id} className="shadow-card border-0 animate-slide-up">
//                 <CardHeader>
//                   <div className="flex items-center justify-between">
//                     <CardTitle className="font-display text-base">
//                       Q{idx + 1}. {question.text}
//                     </CardTitle>
//                     <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">Échelle</Badge>
//                   </div>
//                 </CardHeader>
//                 <CardContent>
//                   <ResponsiveContainer width="100%" height={250}>
//                     <BarChart data={barData}>
//                       <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
//                       <XAxis dataKey="note" stroke="hsl(var(--muted-foreground))" />
//                       <YAxis stroke="hsl(var(--muted-foreground))" />
//                       <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem" }} />
//                       <Bar dataKey="count" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
//                     </BarChart>
//                   </ResponsiveContainer>
//                 </CardContent>
//               </Card>
//             );
//           }

//           return null;
//         })}

//         {/* Text responses */}
//         {questions.some(q => q.question_type === 'TEXT') && (
//           <Card className="lg:col-span-2 shadow-card border-0 animate-slide-up">
//             <CardHeader>
//               <CardTitle className="font-display text-base">Réponses texte</CardTitle>
//             </CardHeader>
//             <CardContent>
//               <div className="space-y-3">
//                 {questions
//                   .filter(q => q.question_type === 'TEXT')
//                   .flatMap(q => q.responses)
//                   .slice(0, 5)
//                   .map((response, i) => (
//                     <div key={i} className="p-3 rounded-lg bg-muted/50 text-sm text-foreground">
//                       <p className="font-medium text-xs text-muted-foreground mb-1">{response.participant?.prenom} {response.participant?.nom}</p>
//                       <p>"{response.answer_text}"</p>
//                     </div>
//                   ))}
//               </div>
//             </CardContent>
//           </Card>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Results;
